// Profiles (S12–S14, T-701). The public card never carries the birth date, Telegram username
// or contacts: only the computed age.
import { z } from 'zod';

import { isoDateSchema } from './age.js';
import { localizedNameSchema } from './localized.js';

export const DISPLAY_NAME_MAX = 40;
export const ABOUT_MAX = 500;
export const MAX_INTERESTS = 15;
/** Largest photo upload (the client shrinks to ≤ 1280 px first). */
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
export const PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/** Shared by applicant cards (S9) and profiles: rating only from 3 reviews. */
export const userStatsSchema = z.object({
	/** Average 1–5, one decimal; null until `MIN_REVIEWS_FOR_RATING` reviews exist. */
	rating: z.number().nullable(),
	/** Completed activities attended (not marked no-show). */
	visited: z.number().int(),
	/** Completed activities organized. */
	created: z.number().int(),
});

export const interestSchema = z.object({ slug: z.string(), name: localizedNameSchema });

/** `GET /v1/users/:id`: the same card for your own (S12) and someone else's profile (S13). */
export const profileSchema = z.object({
	id: z.uuid(),
	displayName: z.string(),
	photoUrl: z.url().nullable(),
	age: z.number().int().nullable(),
	city: z.object({ slug: z.string(), name: localizedNameSchema }).nullable(),
	about: z.string().nullable(),
	interests: z.array(interestSchema),
	stats: userStatsSchema,
	isMe: z.boolean(),
});

export const userIdParamsSchema = z.object({ id: z.uuid() });

const nullableText = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.nullable()
		.transform((value) => (value === null || value === '' ? null : value));

/** `PATCH /v1/me`: any subset. `birthDate` is optional and can be changed any time (D48). */
export const updateProfileFieldsSchema = {
	displayName: z.string().trim().min(1).max(DISPLAY_NAME_MAX).optional(),
	about: nullableText(ABOUT_MAX).optional(),
	/** City slug, or null to clear. */
	city: z.string().min(1).max(64).nullable().optional(),
	/** `YYYY-MM-DD`, or null to clear. Private: others see only the age. */
	birthDate: isoDateSchema.nullable().optional(),
};

/** `PUT /v1/me/interests`: replaces the whole set. */
export const setInterestsRequestSchema = z.strictObject({
	slugs: z
		.array(z.string().min(1).max(64))
		.max(MAX_INTERESTS)
		.refine((slugs) => new Set(slugs).size === slugs.length, 'duplicate interests'),
});

/** `GET /v1/interests` (picker, sorted). */
export const interestsResponseSchema = z.object({
	items: z.array(interestSchema.extend({ icon: z.string() })),
});
/** `GET /v1/cities` (active cities). */
export const citiesResponseSchema = z.object({
	items: z.array(z.object({ slug: z.string(), name: localizedNameSchema })),
});

export type Profile = z.infer<typeof profileSchema>;
export type UserStats = z.infer<typeof userStatsSchema>;
export type InterestsResponse = z.infer<typeof interestsResponseSchema>;
export type CitiesResponse = z.infer<typeof citiesResponseSchema>;
